import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  BackupConfigurationDTO,
  BackupConfigurationUpdateDTO,
  BackupHistoryDTO,
  BackupHistoryPage,
} from '../../../types/settings/backup';

interface BackupState {
  form: BackupConfigurationDTO;
  history: BackupHistoryDTO[];
  paginator: BackupHistoryPage | null;
  pageSize: number;
  isLoading: boolean;
  isSubmitting: boolean;
  isRunning: boolean;
  error: string | null;
}

const initialForm: BackupConfigurationDTO = {
  id: null,
  enabled: false,
  frequency: 'DAILY',
  executionTime: '23:00',
  retentionDays: 7,
  backupDirectory: '',
  compressEnabled: true,
  maxFileCount: 10,
  lastExecution: null,
  nextExecution: null,
  createdAt: null,
  updatedAt: null,
};

const initialState: BackupState = {
  form: initialForm,
  history: [],
  paginator: null,
  pageSize: 10,
  isLoading: false,
  isSubmitting: false,
  isRunning: false,
  error: null,
};

const backupSlice = createSlice({
  name: 'backup',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
      state.error = null;
    },
    finishLoading(state) {
      state.isLoading = false;
    },
    setSubmitting(state, action: PayloadAction<boolean>) {
      state.isSubmitting = action.payload;
    },
    setRunning(state, action: PayloadAction<boolean>) {
      state.isRunning = action.payload;
    },
    setConfiguration(state, action: PayloadAction<BackupConfigurationDTO>) {
      state.form = {
        ...initialForm,
        ...action.payload,
      };
      state.isLoading = false;
      state.isSubmitting = false;
      state.error = null;
    },
    updateFormField(
      state,
      action: PayloadAction<{
        field: keyof BackupConfigurationUpdateDTO;
        value: BackupConfigurationUpdateDTO[keyof BackupConfigurationUpdateDTO];
      }>
    ) {
      state.form = {
        ...state.form,
        [action.payload.field]: action.payload.value,
      };
    },
    setHistory(state, action: PayloadAction<BackupHistoryPage>) {
      state.history = action.payload.content;
      state.paginator = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
    },
    setError(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.isLoading = false;
      state.isSubmitting = false;
      state.isRunning = false;
    },
    resetState() {
      return initialState;
    },
  },
});

export const {
  startLoading,
  finishLoading,
  setSubmitting,
  setRunning,
  setConfiguration,
  updateFormField,
  setHistory,
  setPageSize,
  setError,
  resetState,
} = backupSlice.actions;

export default backupSlice.reducer;
